import React, { useRef, useMemo } from 'react';
import { AbsoluteFill, getRemotionEnvironment, useVideoConfig } from 'remotion';
import { Layer } from '../items/layer';
import { EditorStarterItem } from '../items/item-type';
import { TrackType } from '../state/types';
import { useLocalUrls } from '../caching/load-to-blob-url';
import { useForbidScroll } from '../utils/forbid-scroll';
import { getAssetSourceKind } from '../utils/find-asset-by-id';
import { useAllItems, useAssets, useTransitions } from '../utils/use-context';
import { buildRenderableUnits } from './utils/build-transition-groups';
import {
  getPlaybackBoundaryHoldIncomingItemIdByOutgoingItemId,
  getPlaybackBoundaryPremountFramesByItemId,
} from './utils/playback-boundary-premount';
import { TransitionGroupLayer } from './transition-group-layer';
import { GroupLayer } from './group-layer';
import { partitionGroups } from './utils/group-units';
import { isTransitionableItem } from '../timeline/utils/transition-edge-utils';
import {
  GlobalTtsAudioLayer,
  useGlobalTtsItemIds,
} from './global-tts-audio-layer';
import { CapsuleCoverTransitionLayer } from './capsule-cover-transition-layer';
import { PlaybackBoundaryReadinessProvider } from './playback-boundary-readiness';
import { buildContinuousAudioGroups } from './continuous-audio-groups';
import {
  GlobalAudioItemIdsProvider,
  GlobalContinuousAudioLayer,
} from './global-continuous-audio-layer';

const layerWrapperStyle: React.CSSProperties = {
  overflow: 'hidden',
};

/**
 * 트랜지션 적용 가능한 트랙인지 확인
 * 트랙 내 아이템 중 하나라도 transition 가능한 타입(image, video, gif)이 있으면 true
 */
const isTransitionableTrack = (
  track: TrackType,
  items: Record<string, EditorStarterItem>,
): boolean => {
  return track.items.some((itemId) => {
    const item = items[itemId];
    return item && isTransitionableItem(item);
  });
};

const LayersUnmemoized: React.FC<{
  tracks: TrackType[];
  premountRemoteMedia?: boolean;
}> = ({ tracks, premountRemoteMedia = false }) => {
  const layerWrapperRef = useRef<HTMLDivElement | null>(null);
  useForbidScroll(layerWrapperRef);

  const { fps } = useVideoConfig();
  const { items } = useAllItems();
  const { assets } = useAssets();
  const { transitions } = useTransitions();
  const localUrls = useLocalUrls();
  const globalTtsItemIds = useGlobalTtsItemIds(tracks);
  const continuousAudioGroups = useMemo(
    () => buildContinuousAudioGroups({ tracks, items, fps }),
    [fps, items, tracks],
  );
  const continuousAudioItemIds = useMemo(
    () => new Set(continuousAudioGroups.flatMap((group) => group.itemIds)),
    [continuousAudioGroups],
  );

  const visibleTracks = tracks.filter((track) => !track.hidden);
  const hasTransitions = Object.keys(transitions).length > 0;

  // 같은 groupId 아이템을 트랙 횡단으로 묶어 GroupLayer 로 렌더한다(카드=한 덩어리).
  // groupId 가 하나도 없으면 groups=[] → 아래 per-track 경로가 기존과 동일하게 동작.
  const { groups, groupedItemIds } = useMemo(
    () => partitionGroups(visibleTracks, items),
    [visibleTracks, items],
  );

  const boundaryPremountFramesByItemId = useMemo(() => {
    const boundaryFrames = getPlaybackBoundaryPremountFramesByItemId({
      tracks,
      items,
      assets,
      localUrls,
      fps,
      resolveAssetSourceKind: (asset, localUrl) =>
        getAssetSourceKind(asset, localUrl).kind,
    });
    if (!premountRemoteMedia) return boundaryFrames;

    const previewPremountFrames = Math.floor(3 * fps);
    const nextFrames = new Map(boundaryFrames);
    Object.values(items).forEach((item) => {
      if (item.type !== 'video') return;
      nextFrames.set(
        item.id,
        Math.max(nextFrames.get(item.id) ?? 0, previewPremountFrames),
      );
    });
    return nextFrames;
  }, [assets, fps, items, localUrls, premountRemoteMedia, tracks]);

  const boundaryHoldIncomingItemIdByOutgoingItemId = useMemo(() => {
    // export 렌더는 프레임 단위 디코딩이 보장되므로 Player 전용 last-frame
    // fallback을 넣지 않는다.
    if (getRemotionEnvironment().isRendering) return new Map<string, string>();
    return getPlaybackBoundaryHoldIncomingItemIdByOutgoingItemId({
      tracks,
      items,
      assets,
      localUrls,
      fps,
      resolveAssetSourceKind: (asset, localUrl) =>
        getAssetSourceKind(asset, localUrl).kind,
    });
  }, [assets, fps, items, localUrls, tracks]);

  // 각 트랙에 대해 렌더러블 유닛 계산
  const trackRenderableUnits = useMemo(() => {
    if (!hasTransitions) return null;

    const unitsMap = new Map<string, ReturnType<typeof buildRenderableUnits>>();

    tracks.forEach((track) => {
      if (track.hidden) return;
      if (!isTransitionableTrack(track, items)) return;

      const units = buildRenderableUnits(track, items, transitions);
      unitsMap.set(track.id, units);
    });

    return unitsMap;
  }, [tracks, items, transitions, hasTransitions]);

  return (
    <GlobalAudioItemIdsProvider
      ttsItemIds={globalTtsItemIds}
      continuousItemIds={continuousAudioItemIds}
    >
      <PlaybackBoundaryReadinessProvider>
        <AbsoluteFill
          style={layerWrapperStyle}
          ref={layerWrapperRef}
        >
          <GlobalTtsAudioLayer
            tracks={tracks}
            boundaryPremountFramesByItemId={boundaryPremountFramesByItemId}
          />
          <GlobalContinuousAudioLayer
            groups={continuousAudioGroups}
            boundaryPremountFramesByItemId={boundaryPremountFramesByItemId}
          />
          {tracks
            .slice()
            .reverse()
            .map((track) => {
              if (track.hidden) {
                // 숨겨진 트랙이라도 오디오는 재생
                return (
                  <React.Fragment key={track.id}>
                    {track.items.map((itemId) => (
                      <Layer
                        key={itemId}
                        itemId={itemId}
                        trackMuted={track.muted}
                        audioOnly={true}
                        boundaryPremountFor={
                          boundaryPremountFramesByItemId.get(itemId) ?? 0
                        }
                        boundaryHoldUntilItemId={undefined}
                      />
                    ))}
                  </React.Fragment>
                );
              }

              // track 인덱스를 기반으로 zIndex 계산 (위쪽 트랙이 더 높은 zIndex)
              const trackIndex = visibleTracks.indexOf(track);
              const zIndex = visibleTracks.length - trackIndex;

              // 트랜지션 적용 가능하고 트랜지션이 있는 경우
              if (hasTransitions && isTransitionableTrack(track, items)) {
                const units = trackRenderableUnits?.get(track.id);

                if (units && units.length > 0) {
                  return (
                    <React.Fragment key={track.id}>
                      {units.map((unit, idx) => {
                        if (unit.type === 'transition-group') {
                          return (
                            <TransitionGroupLayer
                              key={`group-${unit.startFrame}-${idx}`}
                              group={unit}
                              trackMuted={track.muted}
                              zIndex={zIndex}
                              boundaryPremountFramesByItemId={
                                boundaryPremountFramesByItemId
                              }
                            />
                          );
                        }

                        // 독립 아이템 (그룹 멤버는 GroupLayer 가 렌더하므로 스킵)
                        if (groupedItemIds.has(unit.item.id)) return null;
                        return (
                          <Layer
                            key={unit.item.id}
                            itemId={unit.item.id}
                            trackMuted={track.muted}
                            zIndex={zIndex}
                            boundaryPremountFor={
                              boundaryPremountFramesByItemId.get(
                                unit.item.id,
                              ) ?? 0
                            }
                            boundaryHoldUntilItemId={boundaryHoldIncomingItemIdByOutgoingItemId.get(
                              unit.item.id,
                            )}
                          />
                        );
                      })}
                    </React.Fragment>
                  );
                }
              }

              // 기존 방식으로 렌더링 (오디오, 텍스트, 트랜지션 없는 경우 등)
              // 그룹 멤버는 GroupLayer 가 렌더하므로 per-track 에서 제외.
              return (
                <React.Fragment key={track.id}>
                  {track.items
                    .filter((itemId) => !groupedItemIds.has(itemId))
                    .map((itemId) => {
                      return (
                        <Layer
                          key={itemId}
                          itemId={itemId}
                          trackMuted={track.muted}
                          zIndex={zIndex}
                          boundaryPremountFor={
                            boundaryPremountFramesByItemId.get(itemId) ?? 0
                          }
                          boundaryHoldUntilItemId={boundaryHoldIncomingItemIdByOutgoingItemId.get(
                            itemId,
                          )}
                        />
                      );
                    })}
                </React.Fragment>
              );
            })}
          {groups.map((group) => (
            <GroupLayer
              key={group.groupId}
              group={group}
            />
          ))}
          <CapsuleCoverTransitionLayer
            transitions={transitions}
            tracks={tracks}
          />
        </AbsoluteFill>
      </PlaybackBoundaryReadinessProvider>
    </GlobalAudioItemIdsProvider>
  );
};

export const Layers = React.memo(LayersUnmemoized);
